import { useEffect, useRef, useState } from 'react';
import { Button } from '@safetyhub/ui';
import { adminApi, extractApiError, type ReleaseNotePlatform } from '../lib/api';
import { useToastStore } from '../stores/toast';
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  platformColor,
  platformLabel,
  toKSTDateString,
  type Category,
} from '../lib/releaseNoteDisplay';

/**
 * 이 화면이 그리는 행 — 관리 뷰의 계약과 **의도적으로 좁다**.
 *
 * `status`·`audiences`는 응답에 실리지만(서버가 행을 그대로 돌려준다) **여기서 읽지 않는다**:
 * status는 서버가 `PUBLISHED`로 무조건 좁히므로 칩이 모든 행에 같은 값을 띄울 뿐이고, 노출 대상은
 * **관리 축**이다. 그 둘을 읽고 싶어지면 그것은 이 화면이 관리 뷰가 되려는 신호다 — 멈추고 결정
 * 기록(`2026-10-21-release-note-surfaces-split-…`)의 ⑥을 읽을 것.
 *
 * ⚠️ 그렇다고 `lib/api.ts`의 응답 선언을 좁히지 말 것 — 그러면 「준다고 선언한 것」이 응답보다
 * 좁아 그 어긋남을 아무도 재지 않는다(그 함수 JSDoc의 같은 규율).
 */
interface FeedNote {
  id: string;
  title: string;
  content: string | null;
  /**
   * surface **집합** — 표시는 **이 칸**을 읽는다.
   *
   * ⚠️ 대표값 `platform`(= `sorted(platforms)[0]`)을 읽지 말 것. 관리 뷰가 그 함정을 겪은 자리가
   * 있고(prod 실측 `ADMIN` 축 **108건** / 분모 1506행 — 서버 `findAll`의 `where` 주석) 여기서
   * 같은 실수를 하면 「앱 얘기」 노트가 「웹」 칩으로 보인다.
   */
  platforms: ReleaseNotePlatform[];
  category: Category;
  releasedAt: string;
  version: string | null;
}

/**
 * 한 장의 크기 — 관리 뷰와 **같은 값**(서버 `RELEASE_NOTES_LIST_MAX_LIMIT` = 100)이다.
 *
 * ⚠️ 올리면 **400**이다(`@Max(RELEASE_NOTES_LIST_MAX_LIMIT)` — 여유분이 0이다). 내리는 것은
 * 안전하지만, 같은 값을 쓰는 이유는 두 화면이 같은 페이징 기제를 공유해 「어느 쪽 상한인가」를
 * 묻는 자리를 만들지 않는 것이다.
 */
const PAGE_SIZE = 100;

/**
 * **업데이트 내역**(소비 뷰) — 수퍼관리자가 **자기에게 보이는** 제품 변경 내역을 읽는 화면.
 * 결정 `docs/decisions/2026-10-21-release-note-surfaces-split-anonymous-visibility-from-consumer-reach.md`
 * 채택 ⑥.
 *
 * ## 🔴 이 화면과 「업데이트 내역 관리」의 차이 — 셋이고 전부 의도다
 * | | 이 화면 | `ReleaseNotes.tsx`(관리) |
 * |---|---|---|
 * | 권한 | **없음**(수퍼관리자 전원) | `system-settings` |
 * | 집합 | `PUBLISHED` ∧ 소비 surface(`INTERNAL` 제외) | **전부**(숨긴 노트·`INTERNAL` 포함) |
 * | 쓰기 | 없음 | 숨김·복구 · 노출 대상 |
 *
 * ⚠️ **여기에 숨김·복구·노출 대상을 더하지 말 것** — 그 축은 `system-settings` 뒤에 있어야 하고,
 * 이 화면은 그 권한을 요구하지 않으므로 더하는 순간 **게이트를 우회하는 입구**가 된다. 서버 쪽
 * 짝 탐지기는 `release-notes.controller.spec.ts`의 `[되돌림] 소비 피드는 조회 1건뿐이다`다.
 *
 * ## ⚠️ 집합을 좁히는 것은 **서버**다 — 화면에서 다시 거르지 말 것
 * `status`·surface 무조건항은 `ReleaseNotesService.findConsumerFeed`가 들고, 그것이 유일한
 * 집행점이다. 여기서 `items.filter(…)`로 한 번 더 거르면 집행처가 둘이 되어 **어느 쪽이 거른
 * 것인지** 알 수 없고, 서버 쪽을 지워도 화면이 멀쩡해 보여 **그 유출이 조용해진다**.
 *
 * ## 필터·선택을 두지 않았다 — 되짚을 조건
 * 이 화면의 목적은 **읽는 것**이라 「고르는」 장치가 필요 없다(관리 뷰의 체크박스·필터는 **숨길
 * 행을 고르기 위한** 것이다). 분류·surface 필터를 넣고 싶어지면 서버는 이미 받으므로
 * (`getReleaseNoteFeed`의 `platform`·`category`) **화면만 더하면 된다** — 그때 관리 뷰의
 * `PLATFORM_FILTER_OPTIONS`를 **공용으로 올려** 쓰고(사본을 만들지 말 것) 그 파일의 「옮기지 않은
 * 것」 절을 다시 판정하라. **되짚을 조건**: 운영자가 「원하는 항목을 찾기 어렵다」고 말할 때.
 */
export default function ReleaseNoteFeed() {
  const { showToast } = useToastStore();
  const [notes, setNotes] = useState<FeedNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  /**
   * 다음 장의 커서 — `null`이면 **마지막 장**이고 「더 보기」를 **렌더하지 않는다**(비활성이 아니다).
   * 서버가 `take: limit + 1`로 한 행 더 읽어 판정한 값이라 **이것이 「더 있나」의 정본**이다
   * (행 수로 되짚지 말 것 — 관리 뷰가 같은 규약을 쓴다).
   */
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  /**
   * 요청 세대 — 언마운트 뒤 도착한 응답이 상태를 쓰지 않게 한다.
   *
   * ⚠️ 관리 뷰와 달리 **필터가 없으므로** 이 카운터가 막는 것은 「필터 재전환」이 아니라 **언마운트
   * 경합** 하나다. 그래서 「심층 방어」이고 오늘 소유자가 없다 — 지워도 red가 되는 것은 없다.
   * 되살아날 조건은 **이 화면에 필터가 생기는 날**이고(위 「되짚을 조건」) 그때 관리 뷰의
   * `reloadToken` 규약을 그대로 가져올 것.
   */
  const requestGenerationRef = useRef(0);

  const loadFirstPage = async () => {
    const generation = ++requestGenerationRef.current;
    const isStale = () => generation !== requestGenerationRef.current;
    try {
      setLoading(true);
      setNextCursor(null);
      const response = await adminApi.getReleaseNoteFeed({ limit: PAGE_SIZE });
      if (isStale()) return;
      if (response.data) {
        setNotes(response.data.items);
        setNextCursor(response.data.nextCursor);
        setTotal(response.data.total);
      }
    } catch (error) {
      if (isStale()) return;
      showToast(extractApiError(error).message, 'error');
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor) return;
    const generation = requestGenerationRef.current;
    try {
      setLoadingMore(true);
      const response = await adminApi.getReleaseNoteFeed({
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      if (generation !== requestGenerationRef.current) return;
      if (response.data) {
        const page = response.data.items;
        // 중복 id를 접는다 — 커서 경계에서 같은 행이 두 번 올 수 있고, 그러면 React가 중복 key로
        // 행을 조용히 생략해 **빠진 것처럼** 보인다(관리 뷰가 같은 처방을 쓴다).
        setNotes((prev) => {
          const seen = new Set(prev.map((n) => n.id));
          return [...prev, ...page.filter((n) => !seen.has(n.id))];
        });
        setNextCursor(response.data.nextCursor);
        setTotal(response.data.total);
      }
    } catch (error) {
      showToast(extractApiError(error).message, 'error');
    } finally {
      // 세대를 **보지 않는다** — 보게 하면 언마운트가 아닌 경합에서 버튼이 영구히 잠긴다
      // (관리 뷰의 그 자리 JSDoc이 정본이다).
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    void loadFirstPage();
    return () => {
      requestGenerationRef.current++;
    };
    // 입력이 없는 화면이라 한 번만 돈다 — 재조회 입구를 만들 일이 생기면 관리 뷰의 `reloadToken`
    // 규약을 가져올 것(그 상수 JSDoc).
    // ⚠️ `eslint-disable react-hooks/exhaustive-deps`를 달지 않는다 — admin eslint에는 react-hooks
    //    플러그인이 **없어** 그 경고가 애초에 뜨지 않고(관리 뷰의 같은 자리 주석), 없는 규칙에 대한
    //    disable은 미사용 directive가 된다. web 짝 파일들은 플러그인이 있어 그것을 단다.
  }, []);

  // 날짜별 그룹핑 (KST 기준) — 관리 뷰와 같은 변환을 쓴다(`lib/releaseNoteDisplay.ts`).
  const groupedNotes = notes.reduce(
    (acc, note) => {
      const date = toKSTDateString(note.releasedAt);
      if (!acc[date]) acc[date] = [];
      acc[date].push(note);
      return acc;
    },
    {} as Record<string, FeedNote[]>,
  );

  const sortedDates = Object.keys(groupedNotes).sort((a, b) => b.localeCompare(a));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">업데이트 내역</h1>
        <span className="text-gray-500 text-sm">총 {total}건</span>
      </div>

      {/*
        안내 — 이 화면이 **무엇을 보여주지 않는지**를 말한다. 없으면 운영자가 「내가 올린 내부 공지가
        안 보인다」·「숨긴 노트가 어디 갔나」에서 막히고, 그때 원인(다른 화면이다)을 짐작할 길이 없다.
        ⚠️ 「관리」로 가는 링크를 두지 않았다 — 그 화면은 `system-settings` 권한 뒤라 **권한이 없는
        운영자에게는 그 메뉴가 뜨지 않는다**. 링크를 두면 그 사람에게 403으로 가는 문을 보여주는
        것이고, 메뉴 자체가 권한을 따르는 것이 이 앱의 관례다(`components/Layout.tsx`).
      */}
      <div className="mb-4 rounded-card border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
        게시된 업데이트 내역만 보입니다 — 「내부」 전용 항목과 숨긴 항목은 이 목록에 포함되지
        않습니다. 그 둘을 보거나 숨김을 되돌리는 것은 「업데이트 내역 관리」에서 합니다.
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">불러오는 중...</div>
      ) : sortedDates.length === 0 ? (
        <div className="text-center py-8 text-gray-500">표시할 업데이트 내역이 없습니다.</div>
      ) : (
        <div className="space-y-4">
          {sortedDates.map((date) => (
            <div key={date} className="border rounded-card overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 font-medium flex items-center gap-2">
                {date}
                <span className="text-gray-500 text-sm">({groupedNotes[date].length}개)</span>
              </div>
              <div className="divide-y">
                {groupedNotes[date].map((note) => (
                  <div key={note.id} className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {/*
                        surface **집합** — 원소마다 칩 하나다(`note.platform` 단수를 읽지 말 것).
                        ⚠️ key에 index를 섞는다 — 중복 원소(`[WEB, WEB]`)는 import 결함이지만 **올 수
                        있고**, 값만 key로 쓰면 React가 중복 key에서 칩을 조용히 생략해 그 결함이
                        화면에서 사라진다(그러면 탐지기가 아니라 은폐다 — 관리 뷰의 같은 주석).
                        ⚠️ 빈 집합에 **특별한 칩을 두지 않았다** — 그 상태는 결함이고 그것을 **보이게
                        하는 것은 관리 뷰의 일**이다(`EMPTY_PLATFORMS_LABEL`). 게다가 빈 집합 노트는
                        서버 무조건항(`hasSome`)에 걸려 **이 피드에 도달조차 못 한다** ⇒ 여기 두면
                        영영 발동하지 않는 분기이고 그것은 거짓 안심이다.
                      */}
                      <span className="flex items-center gap-1">
                        {note.platforms.map((p, i) => (
                          <span
                            key={`${p}-${i}`}
                            className={`px-2 py-0.5 text-xs rounded ${platformColor(p)}`}
                          >
                            {platformLabel(p)}
                          </span>
                        ))}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs rounded ${CATEGORY_COLORS[note.category]}`}
                      >
                        {CATEGORY_LABELS[note.category]}
                      </span>
                      <span className="flex-1 font-medium">{note.title}</span>
                      {note.version && (
                        <span className="text-gray-500 text-sm">v{note.version}</span>
                      )}
                    </div>
                    {/*
                      본문 — **관리 뷰에는 없는 것**이다. 그쪽은 「숨길 행을 고르는」 목록이라 제목만
                      있으면 되지만, 이 화면은 **읽는 것이 목적**이라 본문이 없으면 제목 목록일 뿐이다.
                      `whitespace-pre-wrap`으로 노트 파일의 줄바꿈을 보존한다(마크다운을 렌더하지
                      않는다 — 노트 본문은 평문 규약이다: `release-notes/README.md`).
                    */}
                    {note.content && (
                      <p className="mt-1 ml-1 whitespace-pre-wrap text-sm text-gray-700">
                        {note.content}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/*
            「더 보기」 — `nextCursor`가 `null`이면 **렌더하지 않는다**(비활성이 아니라 없앤다).
            「존재가 상태를 뜻하는 요소는 조건부 렌더」가 이 레포 관례다.
          */}
          {nextCursor && (
            <div className="flex justify-center pt-2">
              <Button variant="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? '불러오는 중...' : '더 보기'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
